import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  ChartBarIcon, CubeIcon, ShoppingBagIcon, CreditCardIcon,
  ArrowUturnLeftIcon, ChatBubbleLeftRightIcon, TagIcon, RectangleStackIcon,
  UsersIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon,
  Bars3Icon, ArrowUpTrayIcon, BellIcon, StarIcon,
} from '@heroicons/react/24/outline';
import { toggleSidebar, setMobileSidebar } from '../../store/slices/uiSlice';
import { logoutThunk, selectUser } from '../../store/slices/authSlice';
import { Avatar } from '../common/UI';
import { productsAPI } from '../../services/api';
import { useQuery } from '../../hooks';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const NAV_ITEMS = [
  { to: '/business',             label: 'Dashboard',    icon: ChartBarIcon,    exact: true },
  { to: '/business/products',    label: 'Products',     icon: CubeIcon },
  { to: '/business/inventory',   label: 'Inventory',    icon: TagIcon },
  { to: '/business/banners',    label: 'Banner',       icon: CubeIcon },
  { to: '/business/upload',      label: 'Bulk Upload',  icon: ArrowUpTrayIcon },
  { to: '/business/orders',      label: 'Orders',       icon: ShoppingBagIcon },
  { to: '/business/payouts',     label: 'Payouts',      icon: CreditCardIcon },
  { to: '/business/returns',     label: 'Returns',      icon: ArrowUturnLeftIcon },
  { to: '/business/reviews',     label: 'Reviews',      icon: StarIcon },
  { to: '/business/categories',  label: 'Categories',   icon: RectangleStackIcon }, 
  { to: '/business/inquiry', label: 'Inquiries', icon: UsersIcon},
  { to: '/business/staff',       label: 'Staff',        icon: UsersIcon },
  { to: '/business/settings',    label: 'Settings',     icon: Cog6ToothIcon },
];

const SideLink = ({ to, icon: Icon, label, exact, onClick }) => (
  <NavLink to={to} end={exact} onClick={onClick}
    className={({ isActive }) => clsx('nav-link', isActive && 'active')}>
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span>{label}</span>
  </NavLink>
);

const SidebarContent = ({ business, onLinkClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user     = useSelector(selectUser);

  const handleLogout = async () => {
    await dispatch(logoutThunk());
    navigate('/login');
  };

  return (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="border-b border-surface-100 px-4 py-5">
        <div className="flex items-center gap-3">
          {business?.logo_url
            ? <img src={business.logo_url} alt={business.name} className="h-8 w-8 rounded-xl object-cover" />
            : (
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-purple-600">
                <span className="text-xs font-bold text-white">{business?.name?.[0] || 'B'}</span>
              </div>
            )}
          <div className="min-w-0">
            <p className="truncate text-sm font-display font-bold text-surface-900">{business?.name || 'My Business'}</p>
            <p className="text-[10px] font-medium text-surface-400 uppercase tracking-widest">Seller Portal</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5 scrollbar-thin">
        {NAV_ITEMS.map(item => (
          <SideLink key={item.to} {...item} onClick={onLinkClick} />
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-surface-100 p-3">
        <div className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-surface-50 transition-colors">
          <Avatar name={`${user?.first_name} ${user?.last_name}`} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="truncate text-xs font-semibold text-surface-800">{user?.first_name} {user?.last_name}</p>
            <p className="truncate text-[10px] text-surface-400 capitalize">{user?.role?.replace(/_/g,' ')}</p>
          </div>
          <button onClick={handleLogout} className="p-1 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-danger-600 transition-colors">
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { data: alerts, refetch } = useQuery(() => productsAPI.lowStockAlerts(), [], { 
    enabled: true 
  });

  useEffect(() => {
    const handleUpdate = () => refetch();
    window.addEventListener('inventoryUpdated', handleUpdate);
    return () => window.removeEventListener('inventoryUpdated', handleUpdate);
  }, [refetch]);

  const count = alerts?.length || 0;

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="btn btn-ghost p-2 rounded-xl relative">
        <BellIcon className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-danger-500 text-[10px] font-bold text-white ring-2 ring-white">
            {count}
          </span>
        )}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right rounded-2xl bg-white p-2 shadow-2xl ring-1 ring-black/5 focus:outline-none z-50">
          <div className="px-3 py-2 border-b border-surface-50 mb-1">
            <h3 className="text-sm font-bold text-surface-900">Notifications</h3>
          </div>
          <div className="max-h-[400px] overflow-y-auto scrollbar-thin">
            {count === 0 ? (
              <div className="py-8 text-center">
                <BellIcon className="h-8 w-8 mx-auto text-surface-200 mb-2" />
                <p className="text-sm text-surface-400">No new alerts</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <Menu.Item key={alert.id}>
                  {({ active }) => (
                    <div className={clsx(
                      'flex items-start gap-3 p-3 rounded-xl transition-colors cursor-default',
                      active ? 'bg-surface-50' : 'bg-white'
                    )}>
                      <div className="mt-1 h-2 w-2 rounded-full bg-danger-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-surface-900 truncate">{alert.display_name}</p>
                        <p className="text-xs text-surface-500 mt-0.5">
                          Low stock: <span className="font-bold text-danger-600">{alert.stock_qty}</span> left (Alert threshold: {alert.low_stock_alert})
                        </p>
                      </div>
                    </div>
                  )}
                </Menu.Item>
              ))
            )}
          </div>
          {count > 0 && (
            <div className="mt-1 p-1 border-t border-surface-50">
              <button 
                onClick={() => navigate('/business/inventory')}
                className="w-full py-2 text-xs font-bold text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
              >
                Manage Inventory
              </button>
            </div>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export const BusinessShell = ({ children, business }) => {
  const dispatch      = useDispatch();
  const sidebarOpen   = useSelector(s => s.ui.sidebarOpen);
  const mobileSidebar = useSelector(s => s.ui.mobileSidebar);
  const user          = useSelector(selectUser);

  return (
    <div className="flex h-screen overflow-hidden bg-surface-50">
      <aside className={clsx('hidden lg:flex flex-col flex-shrink-0 bg-white border-r border-surface-100 transition-all duration-300 overflow-hidden', sidebarOpen ? 'w-[260px]' : 'w-0')}>
        <SidebarContent business={business} />
      </aside>

      {mobileSidebar && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => dispatch(setMobileSidebar(false))} />
          <aside className="absolute left-0 top-0 h-full w-[260px] bg-white shadow-2xl">
            <SidebarContent business={business} onLinkClick={() => dispatch(setMobileSidebar(false))} />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b border-surface-100 bg-white px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => dispatch(toggleSidebar())} className="hidden lg:flex btn btn-ghost p-2 rounded-xl">
              <Bars3Icon className="h-5 w-5" />
            </button>
            <button onClick={() => dispatch(setMobileSidebar(true))} className="lg:hidden btn btn-ghost p-2 rounded-xl">
              <Bars3Icon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-surface-100">
              <Avatar name={`${user?.first_name} ${user?.last_name}`} size="sm" />
              <p className="hidden md:block text-xs font-semibold text-surface-800">{user?.first_name}</p>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 lg:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default BusinessShell;
